import type { FormState } from "./formUtils";
import { bankHatAngaben, kontoinhaberVorschlag } from "./kontoinhaber";
import type { OwnershipPayload } from "./buildPrivateContactCreatePayload";
import {
  commDbIdsAus,
  dbIdAus,
  foUserIdsFuerPayload,
  wegVergleichsSchluessel,
  wegWertFuerSpeichern,
} from "./buildPrivateContactCreatePayload";
import {
  herkunftDetailsFuerSpeichern,
  herkunftDetailsLesen,
  herkunftFreitextFuerSpeichern,
  herkunftZusammensetzen,
} from "./kontaktherkunft";
import { istRollenGruppe } from "../data/rollenKatalog";
import { zuwendungenAusFeld, zuwendungenFuerApi } from "../data/zuwendungenForm";
import { rollenAusFeld, statusFuerApi } from "../data/kontaktstatusSegment";
import { eigenerNewsletterPayload } from "./eigenerNewsletter";

export type AddressPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  addressDescription: string;
  street: string;
  postalCode: string;
  city: string;
  ortsteil: string;
  country: string;
  type: string;
  isDefault: boolean;
};

export type CommunicationPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  type: string;
  label: string;
  value: string;
  isDefault: boolean;
};

export type CompanyContactCreatePayload = {
  basePayload: Record<string, unknown>;
  payloadWithDetails: Record<string, unknown>;
  addresses: AddressPayload[];
  companyCommunications: CommunicationPayload[];
  // Separat exportiert, damit der Staged-Fallback die Banken über den
  // :id/bank-Endpoint nachreichen kann (basePayload trägt KEINE bankAccounts).
  bankAccounts: BankPayload[];
  hasCompanyPhone: boolean;
  hasCompanyEmail: boolean;
  hasCompanyCommunications: boolean;
};

const toTrimmedString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const firstNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = toTrimmedString(value);
    if (normalized) return normalized;
  }
  return "";
};

const toBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  const normalized = toTrimmedString(value).toLowerCase();
  return normalized === "yes" || normalized === "ja" || normalized === "true";
};

/**
 * Zusätzliche Kommunikationswege — aus „Daten befüllen" und aus dem Popup
 * „+ Weiterer Kommunikationsweg".
 *
 * Die Maske hat je Art EIN Feld, `ContactCommunication` aber beliebig viele
 * Zeilen — und ein Impressum nennt regelmäßig mehrere (Zentrale und Vertrieb,
 * info@ und datenschutz@). Der Befüllen-Dialog legt sie deshalb als JSON in
 * `weitereKommunikation` ab; hier werden sie zu Zeilen, die NICHT `isDefault`
 * sind: Die Hauptangabe bleibt die aus dem Maskenfeld.
 *
 * `belegt` sind die Werte, die schon als Standardzeile mitgehen — sonst stünde
 * dieselbe Nummer zweimal in der frisch angelegten Akte.
 */
/** Arten, die als weitere Kommunikationswege gespeichert werden. */
const WEG_ARTEN = new Set(["phone", "mobile", "fax", "email"]);

const parseWeitereKommunikation = (
  formFields: FormState,
  belegt: Array<string | undefined>,
): CommunicationPayload[] => {
  let roh: unknown;
  try {
    roh = JSON.parse(String((formFields as Record<string, unknown>).weitereKommunikation ?? "[]"));
  } catch {
    return [];
  }
  if (!Array.isArray(roh)) return [];
  const gesehen = new Set(
    belegt.filter(Boolean).map((v) => wegVergleichsSchluessel(String(v))),
  );
  const ERLAUBTE_TYPEN = new Set([
    "phone",
    "fax",
    "email",
    "mobile",
    "linkedin",
    "instagram",
    "facebook",
    "tiktok",
    "xing",
    "youtube",
    "homepage",
  ]);
  const zeilen: CommunicationPayload[] = [];
  for (const eintrag of roh) {
    const e = (eintrag ?? {}) as { type?: unknown; value?: unknown; label?: unknown; quelle?: unknown; id?: unknown; dbId?: unknown };
    const rawType = toTrimmedString(e.type);
    // Firmennummern sind geschäftlich: eine Durchwahl „-" bleibt.
    const value = wegWertFuerSpeichern(rawType, toTrimmedString(e.value), true, dbIdAus(e.dbId) !== undefined);
    if (!value) continue;
    const type = rawType === "website" ? "homepage" : rawType;
    if (!ERLAUBTE_TYPEN.has(type)) continue;
    // Bestandszeilen (mit dbId) nie als Doppel aussortieren: Fehlt eine Zeile
    // mit id in der Liste, LÖSCHT das Backend sie. Entdoppelt werden nur neue
    // Einträge (T-59-Nachtrag, 01.10.2026).
    const schluessel = wegVergleichsSchluessel(value);
    if (dbIdAus(e.dbId) === undefined && gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    // Eigene Bezeichnung aus dem Popup „+ Weiterer Kommunikationsweg"; Treffer
    // des Impressum-Abgleichs (auch in der Altform ohne id) heißen wie bisher.
    const defaultTypeName =
      rawType === "mobile" ? "Mobil" : rawType === "fax" ? "Fax" : rawType === "email" ? "E-Mail" : "Telefon";
    const label =
      toTrimmedString(e.label) ||
      (e.quelle === "impressum" || !toTrimmedString(e.id) ? "aus dem Impressum" : defaultTypeName);
    const dbId = dbIdAus(e.dbId);
    zeilen.push({ ...(dbId ? { id: dbId } : {}), type, label, value, isDefault: false });
  }
  return zeilen;
};

const parseAdditionalAddresses = (formFields: FormState): AddressPayload[] => {
  const raw = formFields.additionalAddresses;
  if (typeof raw !== "string" || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const normalized: AddressPayload[] = [];

    parsed.forEach((addr) => {
      if (!addr || typeof addr !== "object") return;
      const item = addr as Record<string, unknown>;

      const addressDescription = toTrimmedString(item.addressDescription);
      const street = toTrimmedString(item.street);
      const postalCode = firstNonEmpty(item.postalCode, item.postalcode, item.plz);
      const city = firstNonEmpty(item.city, item.ort);
      const ortsteil = firstNonEmpty(item.ortsteil, item.cityArea);
      const country = firstNonEmpty(item.country, item.land);

      // Land (vorbelegt „Deutschland") und Bezeichnung allein machen noch
      // keine Anschrift.
      const hasAddressData = [street, postalCode, city, ortsteil].some(Boolean);
      if (!hasAddressData) return;

      normalized.push({
        ...(dbIdAus(item.dbId) ? { id: dbIdAus(item.dbId) } : {}),
        addressDescription,
        street,
        postalCode,
        city,
        ortsteil,
        country,
        type: "private",
        isDefault: toBool(item.isDefault),
      });
    });

    return normalized;
  } catch {
    return [];
  }
};

export type BankPayload = {
  /** Datenbank-Id einer Bestandszeile — nur im Bearbeiten-Modus gesetzt. */
  id?: number;
  accountHolder: string;
  iban: string;
  bic: string;
  bankName: string;
  bankDescription: string;
  isDefault: boolean;
  directDebit: boolean;
};

/** Parse the form's `additionalBanks` JSON (the inline bank list) into the
 *  `bankAccounts` create-payload shape. Banks were previously never persisted
 *  on company create. Ensures exactly one default. */
const parseAdditionalBanks = (formFields: FormState, defaultHolder: string): BankPayload[] => {
  const raw = formFields.additionalBanks;
  if (typeof raw !== "string" || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const normalized: BankPayload[] = [];

    parsed.forEach((bank) => {
      if (!bank || typeof bank !== "object") return;
      const item = bank as Record<string, unknown>;

      const iban = toTrimmedString(item.iban);
      const bic = toTrimmedString(item.bic);
      const bankName = firstNonEmpty(item.bankName, item.bankname);
      const accountHolder = toTrimmedString(item.accountHolder) || defaultHolder;
      const bankDescription = firstNonEmpty(item.bankDescription, item.label, item.bankbezeichnung);

      // Der vorbelegte Kontoinhaber allein macht noch keine Bankverbindung —
      // sonst würde jeder angefangene, leere Eintrag gespeichert.
      if (!bankHatAngaben([iban, bic, bankName], toTrimmedString(item.accountHolder), defaultHolder)) return;

      normalized.push({
        ...(dbIdAus(item.dbId) ? { id: dbIdAus(item.dbId) } : {}),
        accountHolder,
        iban,
        bic,
        bankName,
        bankDescription,
        isDefault: toBool(item.isDefault),
        directDebit: item.directDebit === true,
      });
    });

    if (normalized.length && !normalized.some((b) => b.isDefault)) {
      normalized[0].isDefault = true;
    }
    return normalized;
  } catch {
    return [];
  }
};

/**
 * Builds the request payload(s) for creating a company contact.
 *
 * Important: the returned `payloadWithDetails` MUST NOT include both
 * `communications` and a `defaultPhone` that references the same object —
 * the backend's TypeORM cascade then triggers a "Cyclic dependency:
 * ContactCommunication" error during save (Contact ↔ ContactCommunication).
 *
 * Therefore `defaultPhone` is intentionally omitted here; callers should set
 * it via a follow-up PATCH after the contact (and its communications) are
 * persisted and have ids.
 *
 * Returns null if the contact lacks a name or legalForm (validation failure).
 */
export const buildCompanyContactCreatePayload = (
  formFields: FormState,
  ownership?: OwnershipPayload,
  /**
   * `allowIncomplete` für den UPDATE-Fall (Umwandlung Person → Firma).
   *
   * Die Vollständigkeitsprüfung unten gilt der NEUANLAGE: Eine Firma ohne Namen
   * oder Rechtsform soll gar nicht erst entstehen. Beim Umwandeln existiert der
   * Kontakt aber bereits — und eine Person bringt naturgemäß KEINE Rechtsform
   * mit. Ohne dieses Flag liefert der Aufruf dort immer `null`, der Aufrufer
   * überspringt das Speichern und meldet trotzdem Erfolg: Alle Eingaben wären
   * still verloren.
   */
  opts?: { allowIncomplete?: boolean },
): CompanyContactCreatePayload | null => {
  const name = firstNonEmpty(formFields.name, formFields.lastname, formFields.displayName);
  // Fortsetzung eines abgeschnittenen Firmennamens → Contact.suffix
  // (Property = Spalte). Bewusst NICHT in `name`/`displayName` eingerechnet:
  // gespeichert werden beide Teile getrennt, verbunden wird erst zur Anzeige.
  const suffix = toTrimmedString(formFields.suffix);
  // Produktpartner: Kurzform des Namens („GED Leben") und Verweis auf das
  // globale Gesellschaft-Stammdatum. Bei allen anderen Firmen bleiben beide leer.
  const kurzname = toTrimmedString(formFields.kurzname);
  const gesellschaftIdRaw = Number(toTrimmedString(formFields.gesellschaftId));
  const gesellschaftId =
    Number.isFinite(gesellschaftIdRaw) && gesellschaftIdRaw > 0 ? gesellschaftIdRaw : undefined;
  const legalForm = firstNonEmpty(formFields.gesellschaftsform, formFields.legalForm);
  const legalFormIdRaw = toTrimmedString(formFields.legalFormId);
  const legalFormId = Number(legalFormIdRaw);
  const legalFormObject = Number.isFinite(legalFormId) && legalFormId > 0
    ? { id: legalFormId, legalForm }
    : undefined;
  // Rechtsvorgänger (frühere Rechtsform): Katalogverweis wie die Rechtsform.
  // Leer → nicht im Rumpf; der Diff der Akte macht daraus `oldLegalForm: null`.
  const oldLegalFormId = Number(toTrimmedString(formFields.oldLegalFormId));
  const oldLegalFormObject =
    Number.isInteger(oldLegalFormId) && oldLegalFormId > 0 ? { id: oldLegalFormId } : undefined;

  if (!opts?.allowIncomplete && (!name || !legalForm)) {
    return null;
  }

  const seatStreet = firstNonEmpty(formFields.street, formFields.strasse);
  const seatPostalCode = firstNonEmpty(formFields.postalcode, formFields.postalCode, formFields.plz);
  const seatCity = firstNonEmpty(formFields.city, formFields.ort);
  const seatOrtsteil = firstNonEmpty(formFields.cityArea, formFields.ortsteil);
  const seatCountry = firstNonEmpty(formFields.country, formFields.land);

  const companySeatAddress: AddressPayload = {
    ...(dbIdAus(formFields.addressDbId) ? { id: dbIdAus(formFields.addressDbId) } : {}),
    addressDescription: "Unternehmenssitz",
    street: seatStreet,
    postalCode: seatPostalCode,
    city: seatCity,
    ortsteil: seatOrtsteil,
    country: seatCountry,
    type: "private",
    isDefault: true,
  };

  const hasCompanySeat = [seatStreet, seatPostalCode, seatCity, seatOrtsteil, seatCountry].some(Boolean);
  const companyPhone = firstNonEmpty(formFields.phone, formFields.telefon);
  const companyEmail = firstNonEmpty(formFields.email);

  const commIds = commDbIdsAus((formFields as Record<string, unknown>).commDbIds);
  const mitId = (feld: string): { id?: number } =>
    dbIdAus(commIds[feld]) ? { id: dbIdAus(commIds[feld]) } : {};
  const phoneCommunication: CommunicationPayload = {
    ...mitId("phone"),
    type: "phone",
    label: "Zentrale",
    value: companyPhone,
    isDefault: true,
  };
  const emailCommunication: CommunicationPayload = {
    ...mitId("email"),
    type: "email",
    // Früher „Email" (Quelle von 31 nichtssagenden PROD-Labels) — jetzt wie
    // Telefon/Mobil einheitlich „Zentrale"; `kind` bleibt bei Firmen NULL.
    label: "Zentrale",
    value: companyEmail,
    isDefault: true,
  };
  const companyMobile = firstNonEmpty(formFields.mobile, formFields.mobil);
  const mobileCommunication: CommunicationPayload = {
    ...mitId("mobile"),
    type: "mobile",
    label: "Zentrale",
    value: companyMobile,
    isDefault: false,
  };

  const hasCompanyPhone = Boolean(companyPhone);
  const hasCompanyMobile = Boolean(companyMobile);
  const hasCompanyEmail = Boolean(companyEmail);

  // Fax, Website und Social Media werden als ContactCommunication-Zeilen
  // gespeichert (der `type` ist der Kanal). Bisher wurden diese Felder beim
  // Anlegen NIE persistiert — jedes befüllte Feld in die communications-Liste
  // übernehmen (website → homepage; Typen sind gültige CommunicationTypes).
  const extraCommFields: Array<{ key: string; type: string }> = [
    { key: "fax", type: "fax" },
    { key: "website", type: "homepage" },
    { key: "linkedin", type: "linkedin" },
    { key: "instagram", type: "instagram" },
    { key: "facebook", type: "facebook" },
    { key: "tiktok", type: "tiktok" },
    { key: "xing", type: "xing" },
    { key: "youtube", type: "youtube" },
  ];
  const extraCommunications: CommunicationPayload[] = extraCommFields
    .map(({ key, type }) => ({ key, type, value: toTrimmedString((formFields as Record<string, unknown>)[key]) }))
    .filter((c) => c.value)
    .map((c) => ({ ...mitId(c.key), type: c.type, label: c.type, value: c.value, isDefault: false }));

  const companyCommunications: CommunicationPayload[] = [
    ...(hasCompanyPhone ? [phoneCommunication] : []),
    ...(hasCompanyMobile ? [mobileCommunication] : []),
    ...(hasCompanyEmail ? [emailCommunication] : []),
    ...extraCommunications,
    ...parseWeitereKommunikation(formFields, [companyPhone, companyEmail, companyMobile, toTrimmedString(formFields.fax)]),
  ];
  const hasCompanyCommunications = companyCommunications.length > 0;

  const additionalAddresses = parseAdditionalAddresses(formFields);
  // Bankverbindungen = primäre „Bank"-Karte (iban/bic/bankname/accountHolder/
  // bankbezeichnung) + „Weitere Bank"-Repeater (additionalBanks). Bug-Fix: die
  // PRIMÄREN Felder wurden bei der Firmen-Anlage bisher NICHT übernommen (nur
  // der Repeater), sodass eine allein ausgefüllte Haupt-Bankverbindung verloren
  // ging. Bei Personen war das bereits korrekt (buildPrivateContactCreatePayload).
  const primaryIban = toTrimmedString(formFields.iban);
  const primaryBic = toTrimmedString(formFields.bic);
  const primaryBankName = toTrimmedString(formFields.bankname);
  // Kontoinhaber: was getippt wurde, sonst der Firmenname samt Zusatz — ohne
  // Rechtsform-Kürzel (das tragen die Mitarbeiter selbst nach). Seit 02.10.2026
  // wird er GESPEICHERT, nicht mehr nur angezeigt (utils/kontoinhaber.ts).
  const kontoinhaberDefault = kontoinhaberVorschlag(
    { lastname: firstNonEmpty(formFields.name, formFields.companyName, formFields.lastname), suffix },
    true,
  );
  const primaryAccountHolder = toTrimmedString(formFields.accountHolder) || kontoinhaberDefault;
  const primaryBankDescription = toTrimmedString(formFields.bankbezeichnung);
  // Der vorbelegte Kontoinhaber allein macht noch keine Bankverbindung.
  const hasPrimaryBank = bankHatAngaben(
    [primaryIban, primaryBic, primaryBankName],
    toTrimmedString(formFields.accountHolder),
    kontoinhaberDefault,
  );
  const additionalBankAccounts = parseAdditionalBanks(formFields, kontoinhaberDefault);
  const bankAccounts: BankPayload[] = hasPrimaryBank
    ? [
        {
          ...(dbIdAus(formFields.bankDbId) ? { id: dbIdAus(formFields.bankDbId) } : {}),
          accountHolder: primaryAccountHolder,
          iban: primaryIban,
          bic: primaryBic,
          bankName: primaryBankName,
          bankDescription: primaryBankDescription,
          isDefault: true,
          // Die Anlage hat kein Lastschrift-Feld; im Bearbeiten-Modus trägt
          // der State den gespeicherten Wert, damit er nicht auf „nein" fällt.
          directDebit: toBool(formFields.bankDirectDebit),
        },
        // Primärkonto ist der Default → etwaige Default-Markierungen der
        // weiteren Banken zurücksetzen (genau EIN Default-Konto).
        ...additionalBankAccounts.map((b) => ({ ...b, isDefault: false })),
      ]
    : additionalBankAccounts;
  const addresses: AddressPayload[] = hasCompanySeat
    ? [
        companySeatAddress,
        ...additionalAddresses.map((addr) => ({
          ...addr,
          isDefault: false,
        })),
      ]
    : additionalAddresses;

  // Steuer- & Rechtsdaten — Property-Namen matchen die Contact-Entity-Spalten
  // 1:1, damit der Controller sie via Partial<Contact> direkt übernimmt.
  const steuernummer = toTrimmedString(formFields.steuernummer);
  const umsatzsteuernummer = toTrimmedString(formFields.umsatzsteuernummer);
  const register = toTrimmedString(formFields.register);
  // Volle Handelsregisternummer inkl. Registerart, z. B. "HRB 12345".
  const handelsregisterNummer = toTrimmedString(formFields.handelsregisterNummer);
  // Zuständiges Amtsgericht (Registergericht), z. B. "München".
  const registergericht = toTrimmedString(formFields.registergericht);
  const finanzamt = toTrimmedString(formFields.finanzamt);
  // Die Amtsnummer identifiziert das Finanzamt — der Name tut das bei den
  // 22 gleichnamigen Ämtern nicht (siehe forms/widgets/finanzaemter).
  const finanzamtBufa = toTrimmedString(formFields.finanzamtBufa);
  // Branche → Contact.jobIndustry (PROPERTY-Name, Spalte 'industry'). Trägt den
  // Anzeigepfad „E1 / E2 / E3" der 3-Ebenen-Kaskade (vom Formular gespiegelt),
  // damit die Freitext-Detailansicht die Branche zeigt.
  const branche = toTrimmedString(formFields.industry);
  // Strukturierte Branche → Contact.brancheClassId (id eines Katalog-Blattes,
  // Ebene-3-Spezialisierung der Branchenklassifizierung).
  const brancheClassRaw = formFields.brancheClassId;
  const brancheClassId =
    brancheClassRaw != null && brancheClassRaw !== ""
      ? Number(brancheClassRaw)
      : undefined;
  // Strukturierte Branche → Contact.branchengruppeId (id einer Branchengruppe
  // des ZWEI-Ebenen-Katalogs). `brancheClassId` daneben bleibt unberührt: Sie
  // zeigt in den dreistufigen Altkatalog, dessen Ids unabhängig zählen, und
  // wird noch von der Risikoanalyse-Übernahme befüllt. Die Firmen-Anlage
  // selbst schreibt seit 08.09.2026 nur noch die neuen beiden Felder.
  const branchengruppeRaw = formFields.branchengruppeId;
  const branchengruppeId =
    branchengruppeRaw != null && branchengruppeRaw !== ""
      ? Number(branchengruppeRaw)
      : undefined;
  const brancheFreitext = toTrimmedString(formFields.brancheFreitext);
  // Vorsteuerabzugsberechtigt: "Ja"/"Nein" → boolean (oder weglassen).
  const vorsteuerRaw = toTrimmedString(formFields.vorsteuerabzugsberechtigt).toLowerCase();
  const vorsteuer = vorsteuerRaw === "ja" ? true : vorsteuerRaw === "nein" ? false : undefined;
  // Gemeinnützigkeit (nur Verein/Stiftung/Genossenschaft zeigen das Feld):
  // "Ja"/"Nein" → boolean; Bescheid und Zwecke gibt es nur bei „Ja".
  const gemeinnRaw = toTrimmedString(formFields.istGemeinnuetzig).toLowerCase();
  const istGemeinnuetzig = gemeinnRaw === "ja" ? true : gemeinnRaw === "nein" ? false : undefined;
  const freistellungsbescheidBis =
    istGemeinnuetzig === true ? toTrimmedString(formFields.freistellungsbescheidBis) : "";
  const zweckNrAO = istGemeinnuetzig === true ? toTrimmedString(formFields.zweckNrAO) : "";
  // Geschäftsjahr (zwei Selects: Tag 01–31 + Monat 01–12) → Contact.financialYearEnd
  // als "TT.MM" (das EINE „Geschäftsjahr"-Feld; konsolidiert auf financialYearEnd, das im
  // Detail als „Geschäftsjahr" gezeigt wird). KEIN 01.01-Default — nur übernehmen, wenn
  // Tag UND Monat aktiv gewählt sind. geschaeftsjahrIstKalenderjahr (Flag 01.01) bleibt.
  const gjTag = toTrimmedString(formFields.geschaeftsjahrTag);
  const gjMonat = toTrimmedString(formFields.geschaeftsjahrMonat);
  const geschaeftsjahr = gjTag && gjMonat ? `${gjTag}.${gjMonat}` : "";
  const geschaeftsjahrIstKalenderjahr = geschaeftsjahr
    ? gjTag === "01" && gjMonat === "01"
    : undefined;
  // Gründungsdatum (vollständiges Datum YYYY-MM-DD).
  const gruendungsdatum = toTrimmedString(formFields.gruendungsdatum);
  // Netzwerk-Freitext (welches Netzwerk?) — nur übernehmen, wenn „Netzwerk" als
  // Herkunft gewählt ist. Persistiert in Contact.netzwerkInfo.
  const originSelections: string[] = (() => {
    try {
      const parsed = JSON.parse(toTrimmedString(formFields.contactOriginSelections) || "[]");
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
      return [];
    }
  })();
  const netzwerkInfo = originSelections.includes("Netzwerk") ? toTrimmedString(formFields.netzwerkInfo) : "";
  // Die Herkunft selbst — bis 09/2026 blieb von der Frage nur ihre Wirkung
  // übrig (Empfehlungsgeber als Name, Tippgeber/Kundenverbindung als
  // Verknüpfung); die Antwort war danach nicht mehr feststellbar. Jetzt steht
  // sie als „a; b" in Contact.kontaktherkunft und wird in der Akte gezeigt.
  const kontaktherkunft = herkunftZusammensetzen(originSelections);
  const kontaktherkunftSonstiges = herkunftFreitextFuerSpeichern(
    originSelections,
    toTrimmedString(formFields.contactOriginOther),
  );
  const kontaktherkunftDetails = herkunftDetailsFuerSpeichern(
    originSelections,
    herkunftDetailsLesen(formFields.kontaktherkunftDetails),
  );

  // Herkunft & Empfehlungsgeber
  const parseReferrerName = (raw: unknown): string => {
    if (typeof raw !== "string" || !raw.trim()) return "";
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return (
          parsed.displayName ||
          parsed.name ||
          `${parsed.vorname || ""} ${parsed.nachname || ""}`.trim() ||
          ""
        );
      }
    } catch {
      return String(raw).trim();
    }
    return "";
  };
  const empfehlungsgeber = parseReferrerName(formFields.referrerInteressent) || toTrimmedString(formFields.empfehlungsgeber);
  const istSelbstEmpfehlungsgeber =
    formFields.istEmpfehlungsgeber === true ||
    formFields.istEmpfehlungsgeber === "ja" ||
    formFields.istEmpfehlungsgeber === "true";

  // Kontaktstatus (bei „Sonstige" der konkrete Untertyp) — per Name an den Server
  // (resolveRelations → statusName). Das Feld ist im Anlage-Formular das ERSTE und
  // UI-seitig auf „Interessent" vorausgewählt, wird aber nur bei aktivem Klick in den
  // State geschrieben. Damit NIE eine Firma ohne Status entsteht, den sichtbaren
  // Default hart absichern (Sonstige ohne Untertyp → „Sonstige", sonst → „Interessent").
  // Die Anlage-Maske schickt seit 09/2026 eine LISTE von Rollen
  // (`kontaktStatusRollen`), aus der ein Primär-Status und beliebig viele
  // Zusatz-Status werden — die Regel dazu steht in `statusFuerApi`.
  //
  // Der Rückfall darunter ist kein Altlast-Rest: `SinglePageCompanyForm` (Akte)
  // schickt weiterhin Untertyp + Rolle aus zwei Dropdowns. Ohne ihn verlöre sie
  // beim Speichern ihren Status. Bei einer Rollen-Gruppe („Rolle im Schaden" …)
  // ist der Untertyp dort nur der Abzweig; der Gruppenname selbst darf
  // `statusName` nie erreichen, er steht in keinem ContactStatus-Katalog und
  // `resolveRelations` verwürfe ihn ersatzlos.
  const rawStatus = toTrimmedString(formFields.kontaktStatus);
  const rollen = rollenAusFeld(toTrimmedString(formFields.kontaktStatusRollen));
  const altUntertyp = istRollenGruppe(toTrimmedString(formFields.kontaktStatusSonstig))
    ? toTrimmedString(formFields.kontaktStatusRolle)
    : toTrimmedString(formFields.kontaktStatusSonstig);
  const { statusName, weitereStatus: berechneteWeitereStatus } = statusFuerApi({
    kontaktStatus: rawStatus,
    rollen: rollen.length ? rollen : altUntertyp ? [altUntertyp] : [],
    auchKundeOderInteressent: toTrimmedString(formFields.kontaktStatusAuch),
  });

  const weitereStatus = [...berechneteWeitereStatus];
  if (istSelbstEmpfehlungsgeber && statusName !== "Empfehlungsgeber" && !weitereStatus.includes("Empfehlungsgeber")) {
    weitereStatus.push("Empfehlungsgeber");
  }

  // T1 — „persönlicher Kontakt": NUR als Schalter. Alles Weitere setzt der
  // Server (`ContactController.create`). Siehe buildPrivateContactCreatePayload.
  const persoenlich = toTrimmedString(formFields.persoenlich) === "ja";

  // ── Erreichbarkeit ─────────────────────────────────────────────
  const erreichbarkeitAllg = toTrimmedString(formFields.erreichbarkeit);
  const detailErreichbarkeiten: Record<string, string> = {};
  try {
    const rawDetails = JSON.parse(String(formFields.erreichbarkeitenDetail ?? "{}"));
    if (typeof rawDetails === "object" && rawDetails !== null) {
      Object.assign(detailErreichbarkeiten, rawDetails);
    }
  } catch {
    // ignore
  }
  const detailParts: string[] = [];
  const companyFieldLabels: Record<string, string> = {
    phone: "Telefon (Zentrale)",
    mobile: "Mobil",
  };
  for (const [k, v] of Object.entries(detailErreichbarkeiten)) {
    if (typeof v === "string" && v.trim()) {
      detailParts.push(`${companyFieldLabels[k] || k}: ${v.trim()}`);
    }
  }
  try {
    const rawExtra = JSON.parse(String(formFields.weitereKommunikation ?? "[]"));
    if (Array.isArray(rawExtra)) {
      for (const item of rawExtra) {
        if (item && typeof item === "object") {
          const extraErr = toTrimmedString((item as { erreichbarkeit?: unknown }).erreichbarkeit);
          if (extraErr) {
            const lbl = toTrimmedString((item as { label?: unknown }).label) || "Weitere Nummer";
            detailParts.push(`${lbl}: ${extraErr}`);
          }
        }
      }
    }
  } catch {
    // ignore
  }
  const summaryParts: string[] = [];
  if (erreichbarkeitAllg) summaryParts.push(erreichbarkeitAllg);
  if (detailParts.length > 0) summaryParts.push(detailParts.join("; "));
  const erreichbarkeit = summaryParts.length > 0 ? summaryParts.join(" | ").substring(0, 255) : undefined;

  const basePayload: Record<string, unknown> = {
    contactType: "Firma",
    typ: "Firma",
    type: { id: 1, type: "Firma" },
    lastname: name,
    displayName: name,
    ...(suffix ? { suffix } : {}),
    ...(kurzname ? { kurzname } : {}),
    ...(formFields.partnerStatus ? { partnerStatus: String(formFields.partnerStatus) } : {}),
    ...(["Produktpartner", "Kooperationspartner"].includes(statusName) && Object.prototype.hasOwnProperty.call(formFields, "partnerAbrechnungDirekt")
      ? { partnerAbrechnungDirekt: formFields.partnerAbrechnungDirekt === "true" ? true : formFields.partnerAbrechnungDirekt === "false" ? false : null }
      : {}),
    ...(gesellschaftId !== undefined ? { gesellschaftId } : {}),
    legalForm: legalFormObject ?? legalForm,
    ...(oldLegalFormObject ? { oldLegalForm: oldLegalFormObject } : {}),
    // Konkrete Bezeichnung bei „Sonstige"/„Ausländische Rechtsform" — dort sagt
    // der Katalogeintrag allein nichts aus (Contact.rechtsform).
    ...(toTrimmedString(formFields.rechtsform)
      ? { rechtsform: toTrimmedString(formFields.rechtsform) }
      : {}),
    // Kontaktstatus IMMER senden (Default „Interessent"), damit keine statuslose Firma entsteht.
    statusName,
    // Nur mitschicken, wenn es etwas mitzuteilen gibt: `POST /contact`
    // prüft die Liste gegen den Katalog und lehnt Unbekanntes mit 400 ab.
    ...(weitereStatus.length ? { weitereStatus } : {}),
    ...(persoenlich ? { persoenlich: true } : {}),
    ...(seatStreet ? { street: seatStreet } : {}),
    ...(seatPostalCode ? { postalCode: seatPostalCode } : {}),
    ...(seatCity ? { city: seatCity } : {}),
    ...(seatOrtsteil ? { ortsteil: seatOrtsteil } : {}),
    ...(seatCountry ? { country: seatCountry } : {}),
    ...(steuernummer ? { steuernummer } : {}),
    ...(umsatzsteuernummer ? { umsatzsteuernummer } : {}),
    ...(register ? { register } : {}),
    ...(handelsregisterNummer ? { handelsregisterNummer } : {}),
    ...(registergericht ? { registergericht } : {}),
    ...(finanzamt ? { finanzamt } : {}),
    ...(finanzamtBufa ? { finanzamtBufa } : {}),
    // Mitgliedschaften → Contact.memberships (varchar). War bisher nicht im
    // Payload und ging beim Anlegen verloren.
    ...(toTrimmedString(formFields.memberships)
      ? { memberships: toTrimmedString(formFields.memberships) }
      : {}),
    // Bemerkung → Contact.remark (Freitext).
    ...(toTrimmedString(formFields.remark)
      ? { remark: toTrimmedString(formFields.remark) }
      : {}),
    // Erreichbarkeit → Contact.erreichbarkeit (varchar).
    ...(erreichbarkeit ? { erreichbarkeit } : {}),
    // Unternehmensfunktion → Contact.unternehmensfunktion (varchar, „a; b").
    ...(toTrimmedString(formFields.unternehmensfunktion)
      ? { unternehmensfunktion: toTrimmedString(formFields.unternehmensfunktion) }
      : {}),
    // Branche / Vorsteuer / Geschäftsjahr (PROPERTY-Namen, Spalten existieren).
    ...(branche ? { jobIndustry: branche } : {}),
    // Tätigkeitsbeschreibung der HAUPTbranche → Contact.jobOccupation. In der
    // Detailansicht steht sie als „Tätigkeit" neben der Branche; die
    // Beschreibungen der WEITEREN Branchen hängen an ihrer jeweiligen Zeile
    // (ContactBrancheAssignment) und werden nach dem Anlegen nachgereicht.
    ...(toTrimmedString(formFields.taetigkeitsbeschreibung)
      ? { jobOccupation: toTrimmedString(formFields.taetigkeitsbeschreibung) }
      : {}),
    // Verein: „Satzungszweck" statt Tätigkeitsbeschreibung (User 28.09.2026)
    // → Contact.satzungszweck. Eigenes Formularfeld, damit ein Wechsel der
    // Rechtsform keinen der beiden Texte überschreibt.
    ...(toTrimmedString(formFields.satzungszweck)
      ? { satzungszweck: toTrimmedString(formFields.satzungszweck) }
      : {}),
    ...(brancheClassId != null && !Number.isNaN(brancheClassId)
      ? { brancheClassId }
      : {}),
    ...(branchengruppeId != null && !Number.isNaN(branchengruppeId)
      ? { branchengruppeId }
      : {}),
    ...(brancheFreitext ? { brancheFreitext } : {}),
    ...(vorsteuer !== undefined ? { vorsteuerabzugsberechtigt: vorsteuer } : {}),
    ...(istGemeinnuetzig !== undefined ? { istGemeinnuetzig } : {}),
    ...(freistellungsbescheidBis ? { freistellungsbescheidBis } : {}),
    ...(zweckNrAO ? { zweckNrAO } : {}),
    ...(geschaeftsjahrIstKalenderjahr !== undefined ? { geschaeftsjahrIstKalenderjahr } : {}),
    ...(geschaeftsjahr ? { financialYearEnd: geschaeftsjahr } : {}),
    ...(gruendungsdatum ? { gruendungsdatum } : {}),
    ...(netzwerkInfo ? { netzwerkInfo } : {}),
    ...(kontaktherkunft ? { kontaktherkunft } : {}),
    ...(kontaktherkunftSonstiges ? { kontaktherkunftSonstiges } : {}),
    ...(kontaktherkunftDetails ? { kontaktherkunftDetails } : {}),
    // Zuwendungen, Veranstaltungen & Newsletter — seit 27.09.2026 auch bei der
    // Firma. Nur mitschicken, wenn gesetzt (wie in der Personenanlage).
    ...(toTrimmedString(formFields.einladungKennzeichen)
      ? { einladungKennzeichen: toTrimmedString(formFields.einladungKennzeichen) }
      : {}),
    ...(toTrimmedString(formFields.newsletter) === "ja" ? { newsletter: true } : {}),
    ...(toTrimmedString(formFields.newsletter) === "ja" && toTrimmedString(formFields.newsletterEmails)
      ? { newsletterEmails: toTrimmedString(formFields.newsletterEmails) }
      : {}),
    // Eigener Newsletter des Kontakts (User 28.09.2026).
    ...eigenerNewsletterPayload(formFields),
    ...(zuwendungenFuerApi(zuwendungenAusFeld(formFields.zuwendungen)).length
      ? { zuwendungen: zuwendungenFuerApi(zuwendungenAusFeld(formFields.zuwendungen)) }
      : {}),
    ...(empfehlungsgeber ? { empfehlungsgeber } : {}),
    // ── Verantwortlichkeit (RBAC) ──────────────────────────────────────
    // In basePayload, damit auch der Staged-Fallback (postContact(basePayload))
    // die Ownership trägt. Ohne foUserId → accessLevel=TENANT für nicht-gfgl.
    ...(ownership?.foUserId ? { foUserId: ownership.foUserId } : {}),
    ...foUserIdsFuerPayload(formFields, ownership),
    ...(ownership?.boType ? { boType: ownership.boType } : {}),
    ...(ownership?.boType === "TEAM" && ownership.boTeamId
      ? { boTeamId: ownership.boTeamId }
      : {}),
    ...(ownership?.boType === "USER" && ownership.boUserId
      ? { boUserId: ownership.boUserId }
      : {}),
    ...(ownership?.inhouseContactUserId
      ? { inhouseContactUserId: ownership.inhouseContactUserId }
      : toTrimmedString(formFields.inhouseContactUserId)
      ? { inhouseContactUserId: toTrimmedString(formFields.inhouseContactUserId) }
      : {}),
  };

  const payloadWithDetails: Record<string, unknown> = {
    ...basePayload,
    ...(addresses.length ? { addresses } : {}),
    ...(hasCompanyCommunications
      ? { communications: companyCommunications }
      : {}),
    ...(bankAccounts.length ? { bankAccounts } : {}),
  };

  return {
    basePayload,
    payloadWithDetails,
    addresses,
    companyCommunications,
    bankAccounts,
    hasCompanyPhone,
    hasCompanyEmail,
    hasCompanyCommunications,
  };
};
